import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [deleteModal, setDeleteModal] = useState(null);
  const { logout, getAuthHeader, user } = useAuth();
  const navigate = useNavigate();

  const fetchReports = async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (dateFilter) params.data_evento = dateFilter;
      
      const response = await axios.get(`${API}/reports`, {
        headers: getAuthHeader(),
        params
      });
      setReports(response.data);
    } catch (error) {
      console.error('Errore caricamento report:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, dateFilter]);

  const handleDelete = async (reportId) => {
    try {
      await axios.delete(`${API}/reports/${reportId}`, {
        headers: getAuthHeader()
      });
      setReports(reports.filter(r => r.id !== reportId));
      setDeleteModal(null);
    } catch (error) {
      console.error('Errore eliminazione report:', error);
      alert('Errore durante l\'eliminazione del report');
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dashboard">
      {/* Navbar */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center space-x-3">
              <h1 className="text-base font-semibold text-gray-900">BETTI SRL</h1>
              <span className="text-xs text-gray-400">|</span>
              <span className="text-xs text-gray-500">Report Eventi</span>
            </div>
            <div className="flex items-center space-x-4">
              {user?.role === 'admin' && (
                <button
                  onClick={() => navigate('/admin')}
                  className="text-xs text-gray-600 hover:text-gray-900"
                  data-testid="admin-link"
                >
                  Amministrazione
                </button>
              )}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">{user?.username}</span>
                <span className={`px-2 py-0.5 text-xs font-medium ${
                  user?.role === 'admin' ? 'bg-red-100 text-red-700' :
                  user?.role === 'capo_evento' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {user?.role === 'admin' ? 'Admin' :
                   user?.role === 'capo_evento' ? 'Capo' :
                   'View'}
                </span>
              </div>
              <button
                onClick={logout}
                className="text-xs text-gray-500 hover:text-gray-900"
                data-testid="logout-btn"
              >
                Esci
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-semibold text-gray-900">Report Eventi</h2>
          {user?.role !== 'visualizzatore' && (
            <button
              onClick={() => navigate('/nuovo-report')}
              className="px-4 py-2 bg-gray-900 text-white text-sm hover:bg-gray-800"
              data-testid="new-report-btn"
            >
              + Nuovo Report
            </button>
          )}
        </div>

        {/* Filtri */}
        <div className="bg-white border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Cerca per titolo</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cerca evento..."
                className="w-full px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-900"
                data-testid="search-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filtra per data</label>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                placeholder="Seleziona data"
                className="w-full px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-900"
                data-testid="date-filter-input"
              />
            </div>
          </div>
        </div>

        {/* Lista Report */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : reports.length === 0 ? (
          <div className="bg-white border border-gray-200 p-12 text-center">
            <p className="text-sm text-gray-500">Nessun report trovato</p>
            <p className="text-xs text-gray-400 mt-2">Crea il tuo primo report cliccando su "Nuovo Report"</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reports.map((report) => (
              <div key={report.id} className="bg-white border border-gray-200 p-5 hover:border-gray-400 transition" data-testid="report-card">
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-gray-900 mb-3">
                      <span className="text-gray-400 font-normal">#{report.numero_report || '—'}</span>{' '}
                      {report.titolo_evento}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
                      <span>{formatDate(report.data_evento)}</span>
                      <span className="text-gray-400">•</span>
                      <span>{report.turni_personale?.length || 0} persone</span>
                      <span className="text-gray-400">•</span>
                      <span>{report.orario_partenza} - {report.orario_rientro}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => navigate(`/report/${report.id}`)}
                      className="px-4 py-2 text-sm text-gray-700 border border-gray-300 hover:border-gray-900 hover:bg-gray-50 transition"
                      data-testid="view-report-btn"
                    >
                      Visualizza
                    </button>
                    <button
                      onClick={() => {
                        navigate(`/report/${report.id}`);
                        setTimeout(() => window.print(), 500);
                      }}
                      className="px-4 py-2 text-sm text-gray-700 border border-gray-300 hover:border-gray-900 hover:bg-gray-50 transition"
                      data-testid="print-report-btn"
                    >
                      Stampa
                    </button>
                    {user?.role !== 'visualizzatore' && (
                      <>
                        <button
                          onClick={() => navigate(`/modifica-report/${report.id}`)}
                          className="px-4 py-2 text-sm text-gray-700 border border-gray-300 hover:border-gray-900 hover:bg-gray-50 transition"
                          data-testid="edit-report-btn"
                        >
                          Modifica
                        </button>
                        <button
                          onClick={() => setDeleteModal(report)}
                          className="px-4 py-2 text-sm text-red-600 border border-red-200 hover:border-red-600 hover:bg-red-50 transition"
                          data-testid="delete-report-btn"
                        >
                          Elimina
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Conferma Eliminazione */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4 z-50" data-testid="delete-modal">
          <div className="bg-white max-w-md w-full p-6 border border-gray-300">
            <h3 className="text-base font-semibold text-gray-900 mb-2">Conferma Eliminazione</h3>
            <p className="text-sm text-gray-600 mb-6">
              Eliminare il report "{deleteModal.titolo_evento}"?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModal(null)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 hover:border-gray-900"
                data-testid="cancel-delete-btn"
              >
                Annulla
              </button>
              <button
                onClick={() => handleDelete(deleteModal.id)}
                className="px-4 py-2 text-sm bg-red-600 text-white hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;